import { useCallback } from 'react';

import { noticesStore } from 'stores/global';

import { useStore as useMotionEditorStore, useStoreApi as useMotionEditorStoreApi } from '../../motionEditorStore';
import { augmentKeyPosesWithIntervalKeys, AUTO_AUGMENT_INTERVAL_KEYS_ON_REGENERATE } from '../keyPoseIntervalAugmentation';
import {
  selectIntervalStampApiPayloadForReference,
  selectUserKeyPoseApiPayloadForReference,
} from '../keyPoseSelectors';
import { resolveKeyPoseMotionContext } from '../keyPoseMotionUtils';
import { useStore as useKeyPoseStore, useStoreApi as useKeyPoseStoreApi } from '../keyPoseStore';
import { useKeyPoseIntervalAugmentationContext } from './useKeyPoseIntervalAugmentationContext';

export function useKeyPoseGenerationActions() {
  const motionEditorStore = useMotionEditorStore();
  const motionEditorStoreApi = useMotionEditorStoreApi();
  const keyPoseStore = useKeyPoseStore();
  const keyPoseStoreApi = useKeyPoseStoreApi();
  const intervalAugmentation = useKeyPoseIntervalAugmentationContext();
  const { playback, scene } = motionEditorStore.state;
  const { generation } = keyPoseStore.state;
  const hasApiKey = keyPoseStore.state.hasApiKey;
  const motionContext = resolveKeyPoseMotionContext({
    scene,
    playback,
    keyPoseReferenceName: keyPoseStore.state.activeReferenceName,
  });
  const userKeyPoses = selectUserKeyPoseApiPayloadForReference(
    keyPoseStore.state,
    motionContext?.referenceName,
  );
  const canRegenerateKeyPoseMotion = Boolean(
    scene.data &&
      hasApiKey &&
      motionContext &&
      userKeyPoses.length > 0 &&
      generation.status !== 'pending',
  );

  const regenerateKeyPoseMotion = useCallback(async () => {
    if (!scene.data) {
      noticesStore.error('Load a scene before regenerating motion.');
      return null;
    }

    const currentMotionContext = resolveKeyPoseMotionContext({
      scene: motionEditorStoreApi.state.scene,
      playback: motionEditorStoreApi.state.playback,
      keyPoseReferenceName: keyPoseStoreApi.state.activeReferenceName,
    });

    if (!currentMotionContext) {
      noticesStore.error('Select a motion before regenerating.');
      return null;
    }

    if (!hasApiKey) {
      noticesStore.error('Failed to authenticate. Please try again.');
      return null;
    }

    const currentUserKeyPoses = selectUserKeyPoseApiPayloadForReference(
      keyPoseStoreApi.state,
      currentMotionContext.referenceName,
    );
    if (currentUserKeyPoses.length === 0) {
      noticesStore.error('Add a key pose before regenerating.');
      return null;
    }

    const keyPosesForEdit =
      AUTO_AUGMENT_INTERVAL_KEYS_ON_REGENERATE && intervalAugmentation
        ? augmentKeyPosesWithIntervalKeys({
            userKeyPoses: currentUserKeyPoses,
            existingIntervalKeyPoses: selectIntervalStampApiPayloadForReference(
              keyPoseStoreApi.state,
              currentMotionContext.referenceName,
            ),
            ...intervalAugmentation,
          })
        : [
            ...currentUserKeyPoses,
            ...selectIntervalStampApiPayloadForReference(
              keyPoseStoreApi.state,
              currentMotionContext.referenceName,
            ),
          ].sort((a, b) => a.frame - b.frame);

    const motionDescription =
      motionEditorStoreApi.state.motionDescription.trim() || undefined;

    const result = await keyPoseStore.regenerateSceneObjectMotionKeyPoses({
      sceneID: currentMotionContext.sceneID,
      referenceName: currentMotionContext.referenceName,
      motionID: currentMotionContext.motionID,
      timelineIndex: currentMotionContext.timelineIndex,
      keyPoses: keyPosesForEdit,
      prompt: motionDescription,
    });

    if (!result.ok) {
      noticesStore.error(result.error);
      return null;
    }

    return result;
  }, [
    hasApiKey,
    intervalAugmentation,
    keyPoseStore,
    keyPoseStoreApi,
    motionEditorStoreApi,
    scene.data,
  ]);

  return {
    canRegenerateKeyPoseMotion,
    regenerateKeyPoseMotion,
  };
}
